/**
 * A photo for every food on the web (owner, 2026-10-08): taking it (the camera on a phone, a file on a computer), shrinking it
 * to 1600 px on the longest side as a JPEG (the browser applies the camera's rotation; the server strips what's left of the
 * hidden details before it keeps anything), and showing a food's picture by its id (read once, kept as an object URL).
 */
import { useEffect, useState } from "react";
import type { Api, FoodScope } from "../api.ts";

export const FOOD_PHOTO_SIDE = 1600;

/** Shrinks to FOOD_PHOTO_SIDE on the longest side and re-encodes as JPEG. A browser that can't (or a photo already small) sends the file as it is. */
export async function shrinkFoodPhoto(file: Blob, side = FOOD_PHOTO_SIDE, quality = 0.82): Promise<Blob> {
  try {
    if (typeof createImageBitmap === "function" && typeof document !== "undefined") {
      const bmp = await createImageBitmap(file, { imageOrientation: "from-image" } as ImageBitmapOptions);
      const scale = Math.min(1, side / Math.max(bmp.width, bmp.height));
      const canvas = document.createElement("canvas");
      canvas.width = Math.max(1, Math.round(bmp.width * scale)); canvas.height = Math.max(1, Math.round(bmp.height * scale));
      const ctx = canvas.getContext("2d");
      if (ctx) {
        ctx.drawImage(bmp, 0, 0, canvas.width, canvas.height);
        const out = await new Promise<Blob | null>((res) => canvas.toBlob(res, "image/jpeg", quality));
        if (out && out.type === "image/jpeg") return out;
      }
    }
  } catch { /* fall through: send the file as it is */ }
  return file;
}

/** The small picture for the roundup email (about 160 px). undefined where the browser can't make one (then the email has no picture for this food). */
export async function foodThumb(file: Blob): Promise<Blob | undefined> {
  const t = await shrinkFoodPhoto(file, FOOD_THUMB_SIDE, 0.7);
  return t !== file && t.size <= 60_000 ? t : undefined;
}
export const FOOD_THUMB_SIDE = 160;

type PhotoApi = Pick<Api, "foodPhotoImage">;
const urls = new WeakMap<object, Map<string, Promise<string>>>();
/** The picture as an object URL, kept per business and id (an id is a hash of the bytes, so it never changes). */
export function foodPhotoUrl(api: PhotoApi, scope: FoodScope, photoId: string): Promise<string> {
  let m = urls.get(api);
  if (!m) { m = new Map(); urls.set(api, m); }
  const k = `${scope.orgId}|${photoId}`;
  let u = m.get(k);
  if (!u) { u = api.foodPhotoImage(scope, photoId).then((b) => URL.createObjectURL(b)); u.catch(() => m!.delete(k)); m.set(k, u); }
  return u;
}

/** The URL to show (undefined while it loads, or if it can't be read: the row then simply has no picture). `local` is a picture taken on this device and not sent yet. */
export function useFoodPhotoUrl(api: PhotoApi, scope: FoodScope, photoId: string | undefined, local?: string): string | undefined {
  const [url, setUrl] = useState<string>();
  useEffect(() => {
    let live = true;
    setUrl(undefined);
    if (photoId && typeof api.foodPhotoImage === "function") void foodPhotoUrl(api, scope, photoId).then((u) => { if (live) setUrl(u); }, () => {});
    return () => { live = false; };
  }, [api, scope.orgId, scope.home, photoId]); // eslint-disable-line react-hooks/exhaustive-deps
  return local ?? url;
}
